import React, { useState, useEffect, useMemo } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useFormik } from 'formik';
import { useCourse } from '@/hooks/useCourse';
import { EmptyState } from '@/components/custom/empty-state';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import BarChart from './BarChart';
import DataGrid from './DataGrid';
import RecordClassesReportFilter from './RecordClassesReportFilter';
import { Loader } from '@/components/custom/loader';
import { downloadCSV } from '@/utils/DownloadCSV';
import usePermission from '@/hooks/usePermission';
import { ReportKpiValue } from '@/components/custom/report';

export function RecordClassesReport() {
  const navigate = useNavigate();
  const client = useClient();
  const [report, setReport] = useState({});
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [modules, setModules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchModules = async (url = '/learning_systems?simple=true') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar módulos');
    } else {
      setModules(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const xlsxStructure = [
    {
      sheet: 'Acessos às aulas gravadas',
      columns: [
        { label: 'Data', value: 'Data' },
        { label: 'Aluno', value: 'Aluno' },
        { label: 'Email', value: 'Email' },
        { label: 'Escola', value: 'Escola' },
        { label: 'INEP', value: 'INEP' },
        { label: 'GRE', value: 'GRE' },
        { label: 'Aula', value: 'Aula' }
      ],
      content: report?.logs?.map(item => ({
        Data: new Date(item['access-date']).toLocaleDateString('pt-br'),
        Aluno: item.name,
        Email: item.email,
        Escola: item.institution,
        INEP: item.inep,
        GRE: item.gre,
        Aula: item['learning-system-item-body']
      }))
    }
  ];

  const handleDownloadCSV = () => {
    downloadCSV(xlsxStructure, 'relatorio_Aulas_Gravadas');
  };

  const previousMonth = new Date();

  previousMonth.setMonth(new Date().getMonth() - 1);

  const filters = useFormik({
    initialValues: {
      'start-date': previousMonth.toISOString().substring(0, 10),
      'end-date': new Date().toISOString().substring(0, 10)
    }
  });

  const { courses, getSimpleCourses } = useCourse();

  useEffect(() => {
    if (courses.length === 0) {
      getSimpleCourses();
    }
    fetchModules();
  }, []);

  const data = useMemo(() => {
    return [
      {
        placeholder: 'Selecione o curso',
        name: 'Curso',
        options: courses.map(item => ({ value: item.id, label: item.title }))
      },
      {
        placeholder: 'Selecione o módulo',
        name: 'Módulo',
        options: modules
      },
      {
        placeholder: 'Selecione a aula',
        name: 'Aula',
        options: classes.map(item => ({ value: item.id, label: item.body }))
      }
    ];
  }, [courses, modules, classes]);

  useEffect(() => {
    setCurrentFilters([...selectedModules, ...selectedCourses, ...selectedClasses]);
  }, [selectedModules, selectedCourses, selectedClasses]);

  const handleRemoveFilterItem = item => {
    //remover itens do filtro
    switch (item.type) {
      case 'course':
        setSelectedCourses(prevState => prevState.filter(c => c.value !== item.value));
        break;
      case 'module':
        setSelectedModules(prevState => prevState.filter(m => m.value !== item.value));
        break;
      case 'class':
        setSelectedClasses(prevState => prevState.filter(i => i.value !== item.value));
        break;
    }
  };

  const fetchLessons = async () => {
    const { data, error } = await client.fetch('/learning_system_items?simple=true');
    if (error) {
      toast.error('Erro ao buscar aulas');
    } else {
      setClasses(data);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    let url = '/reports/learning_system_item_reports?';
    let params = [];
    if (filters.values['start-date'] && filters.values['end-date']) {
      params.push(`initial_date=${filters.values['start-date']}&final_date=${filters.values['end-date']}`);
    }
    if (selectedCourses.length > 0) {
      params.push(`course_ids=${selectedCourses.map(item => item.value)}`);
    }
    if (selectedModules.length > 0) {
      params.push(`learning_system_ids=${selectedModules.map(item => item.value)}`);
    }
    if (selectedClasses.length > 0) {
      params.push(`learning_system_item_ids=${selectedClasses.map(item => item.value)}`);
    }

    const { data, error } = await client.fetch(url + params.join('&'));
    if (error) {
      toast.error('Erro ao buscar relatório');
    } else {
      setReport(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
  }, [filters.values, currentFilters]);

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <a
            className="card__icon-wrapper"
            onClick={() => navigate(-1)}
            style={{ cursor: 'pointer' }}
          >
            <FiChevronLeft className="card__icon" />
          </a>
          Relatório de Acessos às Aulas Gravadas
        </h1>
        <p className="report__description">Objetivo: Acompanhar quais alunos, perfis, escolas ou GRE estão assistindo as aulas ao gravadas.</p>
      </header>

      <div className="report__body">
        <div className="report__filter report__filter--space report__filter--multi-select">
          <RecordClassesReportFilter
            setSelectedCourses={setSelectedCourses}
            selectedCourses={selectedCourses}
            currentFilters={currentFilters}
            setCurrentFilters={setCurrentFilters}
            setSelectedModules={setSelectedModules}
            setSelectedClasses={setSelectedClasses}
            data={data}
            handleRemoveFilterItem={handleRemoveFilterItem}
          />
          <div
            className="report-widget__filter"
            style={{ display: 'block' }}
          >
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={filters.values['start-date']}
                onChange={e => filters.setFieldValue('start-date', e.target.value)}
                title="Data de início"
                aria-label="Data de início"
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                value={filters.values['end-date']}
                onChange={e => filters.setFieldValue('end-date', e.target.value)}
                min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          </div>
        </div>
        <div className="report-widget">
          <div className="report-widget__body">
            <div className="round-dg-wrapper">
              {loading ? (
                <Loader />
              ) : (
                <>
                  <div
                    className="report-kpi-card-wrapper"
                    style={{ padding: '32px 0' }}
                  >
                    {report?.['total-unique-accesses'] >= 0 && (
                      <div className="report-kpi-card">
                        <span className="report-kpi-card__title">Quantidade total de acessos únicos</span>
                        <ReportKpiValue style={{ color: '#5D6A86', marginTop: '24px' }}>
                          <b>{report?.['total-unique-accesses']}</b>
                        </ReportKpiValue>
                      </div>
                    )}
                    {report?.['average-accesses'] >= 0 && (
                      <div className="report-kpi-card">
                        <span className="report-kpi-card__title">Taxa de visualização</span>
                        <ReportKpiValue style={{ color: '#5D6A86', marginTop: '24px' }}>
                          <b>{report?.['average-accesses'] + '%'}</b>
                        </ReportKpiValue>
                        <p style={{ fontSize: '12px' }}>Porcentagem de alunos que acessaram pelo menos uma aula</p>
                      </div>
                    )}

                    {report?.['taxonomy-most-accessed']?.count && (
                      <div className="report-kpi-card">
                        <span className="report-kpi-card__title">Tópico mais assistido</span>
                        <span>
                          <b className="text-primary-dark">{report?.['taxonomy-most-accessed']?.name ? report?.['taxonomy-most-accessed']?.name : ''}</b>
                        </span>
                        <ReportKpiValue style={{ color: '#5D6A86', marginTop: '24px' }}>
                          <b>{report?.['taxonomy-most-accessed']?.count}</b>
                        </ReportKpiValue>
                      </div>
                    )}
                    {report?.['taxonomy-least-accessed']?.count && report?.['taxonomy-most-accessed']?.name !== report?.['taxonomy-least-accessed']?.name ? (
                      <div className="report-kpi-card">
                        <span className="report-kpi-card__title">Tópico menos assistido</span>
                        <span>
                          <b className="text-primary-dark">{report?.['taxonomy-least-accessed']?.name}</b>
                        </span>
                        <ReportKpiValue style={{ color: '#5D6A86', marginTop: '24px' }}>
                          <b>{report?.['taxonomy-least-accessed']?.count}</b>
                        </ReportKpiValue>
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="report-kpi-card-wrapper"
                    style={{ padding: '32px 0' }}
                  >
                    <div
                      className="report-kpi-card"
                      style={{ width: '500px' }}
                    >
                      <BarChart
                        data={report?.['institution-unique-accesses']}
                        title="Escolas mais presentes"
                      />
                    </div>
                    <div
                      className="report-kpi-card"
                      style={{ width: '500px' }}
                    >
                      <BarChart
                        data={report?.['gre-unique-accesses']}
                        title="GREs mais presentes"
                      />
                    </div>
                  </div>
                  <div
                    className="report-widget__header"
                    style={{ justifyContent: 'flex-end', marginBottom: '24px' }}
                  >
                    <div className="report-widget__filter">
                      {usePermission('Download de relatórios', 'Relatórios') && (
                        <button
                          className="btn btn--outline"
                          type="button"
                          disabled={false}
                          onClick={handleDownloadCSV}
                        >
                          Download do relatório
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {!loading && report.logs?.length > 0 ? (
                <DataGrid
                  setReport={setReport}
                  report={report}
                />
              ) : !loading ? (
                <EmptyState
                  type="content"
                  title="Nenhum dado encontrado"
                  text="Tente buscar por outro curso"
                  variant="plain"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
